/**
 * Housing Unit Detail Plugin for Native Interactive Site Map
 *
 * Owns [data-panel] content when activeView === VIEW_HOUSING_UNIT_DETAIL. For now, only renders
 * the unit's layout image inline (components/floorPlanImage.dom.js) — the rest of this view's
 * content (price, specs, CTAs) is a future ticket. Unlike floorPlanDetail, this view has no tabs
 * and never touches [data-secondary-panel]: the image fits inline on both desktop and mobile
 * because there's no competing tabbed content here — see this folder's own
 * HOUSING_UNIT_DETAIL_PLUGIN_SEQUENCE.md.
 *
 * Leaving this view has no dedicated control here, same as lotMapSelection: tapping the map
 * outside any lot dispatches navigateBack() from lotInteraction.plugin.js, which is
 * view-agnostic and already handles this view correctly.
 *
 * @module nativeInteractiveSiteMap/plugins/housingUnitDetail.plugin
 */

import { distinctUntilChanged, filter, map, pairwise, tap } from 'rxjs/operators';
import { selectActiveView, selectActiveViewHousingUnitId, selectActiveHousingUnit } from '../redux/selectors';
import { VIEW_HOUSING_UNIT_DETAIL } from '../constants';
import renderFloorPlanImage from '../components/floorPlanImage.dom';
import createPanelContent from '../components/panelContent.dom';
import { onViewLeft, isViewActive } from '../services/viewTransition';

function selectViewAndHousingUnit(storeState) {
  return {
    activeView: selectActiveView(storeState),
    housingUnitId: selectActiveViewHousingUnitId(storeState),
  };
}

function viewAndHousingUnitEqual(previous, current) {
  return previous.activeView === current.activeView && previous.housingUnitId === current.housingUnitId;
}

export default function createHousingUnitDetailPlugin() {
  const state = {
    panel: null,
    panelContent: null,
    store$: null,
    getState: null,
    subscriptions: [],
  };

  function mount() {
    if (!state.panel) {
      return;
    }
    const housingUnit = selectActiveHousingUnit(state.getState());
    if (!state.panelContent) {
      state.panelContent = createPanelContent();
      state.panel.appendChild(state.panelContent.getElement());
    }
    state.panelContent.getElement().innerHTML = renderFloorPlanImage({
      imageUrl: housingUnit?.image ?? null,
      imageAlt: housingUnit?.name ? `${housingUnit.name} layout image` : 'layout image',
    });
  }

  function remove() {
    if (state.panelContent) {
      state.panelContent.remove();
      state.panelContent = null;
    }
  }

  return {
    name: 'housing-unit-detail',

    async start(context) {
      state.panel = context.root.querySelector('[data-panel]');
      if (!state.panel) {
        return;
      }
      state.store$ = context.services.store$;
      state.getState = context.services.getState;

      state.subscriptions.push(
        state.store$.pipe(
          map(selectViewAndHousingUnit),
          distinctUntilChanged(viewAndHousingUnitEqual),
          pairwise(),
          tap(onViewLeft(VIEW_HOUSING_UNIT_DETAIL, remove)),
          filter(isViewActive(VIEW_HOUSING_UNIT_DETAIL)),
        ).subscribe(mount),
      );
    },

    async stop() {
      state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
      state.subscriptions = [];
      remove();
      state.panel = null;
      state.store$ = null;
      state.getState = null;
    },
  };
}
